import { useEffect, useRef, useState, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { DropdownItem } from './Dropdown';

export interface ContextMenuProps {
  items: DropdownItem[];
  children: ReactNode;
  disabled?: boolean;
}

/**
 * Right-click context menu. Wraps children w div który słucha onContextMenu,
 * renderuje menu przez portal w pozycji myszy. Zamyka na click outside lub
 * Escape.
 */
export function ContextMenu({ items, children, disabled = false }: ContextMenuProps) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pos) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setPos(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPos(null);
    };
    // Po otwarciu menu - delay na 1 tick żeby obecny click context nie od razu zamknął
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', onClick);
      document.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [pos]);

  const handleContextMenu = (e: React.MouseEvent) => {
    if (disabled) return;
    e.preventDefault();
    // Viewport-aware clamping (prosty: jeśli mielibyśmy wyjść z prawego brzegu,
    // kotwiczymy do prawej krawędzi kursora). Bez floating-ui.
    const menuWidth = 200;
    const menuHeightEstimate = items.length * 36 + 16;
    const x = Math.min(e.clientX, window.innerWidth - menuWidth - 8);
    const y = Math.min(e.clientY, window.innerHeight - menuHeightEstimate - 8);
    setPos({ x, y });
  };

  return (
    <>
      <div onContextMenu={handleContextMenu}>{children}</div>
      {pos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="fixed z-[200] min-w-[10rem] rounded-xl bg-surface-raised border border-border shadow-glass backdrop-blur-xl py-1.5 animate-scale-in origin-top-left"
            style={{ left: pos.x, top: pos.y }}
          >
            {items.map((item) => (
              <div key={item.id}>
                {item.divider && <div className="my-1 h-px bg-border-subtle" />}
                <button
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  onClick={() => {
                    if (!item.disabled) {
                      item.onClick?.();
                      setPos(null);
                    }
                  }}
                  className={[
                    'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors',
                    item.disabled
                      ? 'opacity-50 cursor-not-allowed text-text-disabled'
                      : item.destructive
                        ? 'text-danger hover:bg-danger/10'
                        : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover',
                  ].join(' ')}
                >
                  {item.icon && (
                    <span
                      className="inline-flex items-center justify-center w-4 h-4 shrink-0 [&>svg]:w-full [&>svg]:h-full"
                      aria-hidden="true"
                    >
                      {item.icon}
                    </span>
                  )}
                  <span className="truncate">{item.label}</span>
                </button>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
